import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { PortalLayout } from '@/layouts/portal-layout';
import { OverviewPage } from '@/pages/overview';
import { RepositoriesPage } from '@/pages/repositories';
import { SyncIssuesPage } from '@/pages/sync-issues';
import { ProjectPage } from '@/pages/project';
import { ProjectBrokenPage } from '@/pages/project-broken';
import { ProjectArchivedPage } from '@/pages/project-archived';
import { SearchPage } from '@/pages/search';
import { SettingsPage } from '@/pages/settings';
import { NotFoundPage } from '@/pages/not-found';
import { LoginPage } from '@/pages/login';
import { RegistrationPage } from '@/pages/registration';
import { ErrorPage } from '@/pages/error';
import { RequireAuth } from '@/routes/require-auth';

// RFC-0011 Task 3.2 — code-split decision: `/search` is kept in the MAIN chunk
// (static import), NOT React.lazy-split. Measured: lazy-splitting the route
// trimmed the main chunk by only ~6.9 kB raw / ~1.9 kB gzip (505.1 → 503.2 kB
// gzip, 0.38%) because the globally-mounted Cmd-K palette already pulls in every
// primitive `/search` reuses (useSearch, SearchResultItem, RepoScopeToggle, the
// highlight parser, phosphor icons) — so the split-out module was only ~7 kB and
// did not move the main chunk toward RFC-0010's 500 kB budget. Not worth a
// Suspense boundary + an extra request on first /search visit.

// RFC-0012 Task 2.3 — code-split decision: `/graph` IS lazy-split (the app's
// FIRST React.lazy route). Unlike `/search`, GraphView pulls in the heavy graph
// stack (reagraph + three + @react-three/* + troika), which would regress the
// main chunk for every route. `React.lazy` + the <Suspense> boundary below keep
// that chain in a separate `/graph` chunk, fetched only on first visit.
const GraphView = lazy(() => import('@/pages/GraphView'));

/**
 * Redirect the legacy `/repositories/:repoId[/:docId]` reader routes (RFC-0009's
 * mock doc-reader) to the canonical `/projects/:repo` reader (RFC-0010 §1, O2).
 */
function LegacyRepoRedirect() {
  const { repoId } = useParams();
  return <Navigate to={`/projects/${repoId ?? ''}`} replace />;
}

/** Suspense fallback shown while the lazy `/graph` chunk downloads (RFC-0012). */
function GraphRouteFallback() {
  return (
    <div
      role="status"
      aria-label="Loading dependency graph"
      className="h-[calc(100vh-13rem)] min-h-120 animate-pulse rounded-xl border border-border bg-surface"
    />
  );
}

export function App() {
  return (
    <Routes>
      {/* Public routes — rendered OUTSIDE the portal shell / AuthProvider, so they
          never fire GET /api/me and never flash auth-gated chrome. RFC-0015 Gap C
          adds /registration + /error as siblings of /login (Kratos redirects
          first-time users and self-service errors to these SPA routes). These sit
          disjoint from the Phase 1 RequireAuth-wrapped protected block below. */}
      <Route path="login" element={<LoginPage />} />
      <Route path="registration" element={<RegistrationPage />} />
      <Route path="error" element={<ErrorPage />} />
      <Route element={<PortalLayout />}>
        {/* Gap E (RFC-0015): each protected route element is wrapped in
            <RequireAuth> — a pre-render gate on the resolved AuthProvider state,
            so no protected chrome flashes before the existing 401 → /login
            bounce. RequireAuth adds no fetch/redirect of its own. */}
        <Route index element={<RequireAuth><OverviewPage /></RequireAuth>} />
        <Route path="repositories" element={<RequireAuth><RepositoriesPage /></RequireAuth>} />
        {/* Canonical doc reader (RFC-0010). */}
        <Route path="projects/:repo" element={<RequireAuth><ProjectPage /></RequireAuth>} />
        <Route path="projects/:repo/docs/*" element={<RequireAuth><ProjectPage /></RequireAuth>} />
        {/* State pages — broken-manifest error + archived tombstone (RFC-0010 §8/§9). */}
        <Route path="projects/:repo/broken" element={<RequireAuth><ProjectBrokenPage /></RequireAuth>} />
        <Route path="projects/:repo/archived" element={<RequireAuth><ProjectArchivedPage /></RequireAuth>} />
        {/* Legacy reader routes → redirect to the canonical project reader. */}
        <Route path="repositories/:repoId" element={<RequireAuth><LegacyRepoRedirect /></RequireAuth>} />
        <Route path="repositories/:repoId/:docId" element={<RequireAuth><LegacyRepoRedirect /></RequireAuth>} />
        <Route path="sync-issues" element={<RequireAuth><SyncIssuesPage /></RequireAuth>} />
        <Route path="search" element={<RequireAuth><SearchPage /></RequireAuth>} />
        {/* First lazy route (RFC-0012 §2): Suspense boundary while the graph
            chunk loads on first visit to /graph. */}
        <Route
          path="graph"
          element={
            <RequireAuth>
              <Suspense fallback={<GraphRouteFallback />}>
                <GraphView />
              </Suspense>
            </RequireAuth>
          }
        />
        <Route path="settings" element={<RequireAuth><SettingsPage /></RequireAuth>} />
        <Route path="*" element={<RequireAuth><NotFoundPage /></RequireAuth>} />
      </Route>
    </Routes>
  );
}
